import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageTitle from "./common/PageTitle";
import { apiService } from "../api/services/apiService";
import {
  DataSensitivity,
  EntityStatus,
  KafkaSchemaFormat,
  SchemaCompatibilityLevel,
} from "../types";
import { PM_HUBS } from "../utils/pmHubRoutes";

interface AddKafkaEventTopicProps {
  tenantId: string | null;
  returnPath?: string;
  returnSearch?: string;
}

const SCHEMA_FORMATS = Object.values(KafkaSchemaFormat);
const COMPATIBILITY_LEVELS = Object.values(SchemaCompatibilityLevel);
const SENSITIVITY_LEVELS = Object.values(DataSensitivity);

const AddKafkaEventTopic: React.FC<AddKafkaEventTopicProps> = ({
  tenantId,
  returnPath,
  returnSearch,
}) => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const goBack = () => {
    if (returnPath) {
      const search = returnSearch?.startsWith("?")
        ? returnSearch
        : returnSearch
          ? `?${returnSearch}`
          : "";
      navigate(`${returnPath}${search}`);
      return;
    }
    navigate(PM_HUBS.kafkaEvent);
  };

  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState("prod");
  const [purpose, setPurpose] = useState("");
  const [description, setDescription] = useState("");
  const [classifications, setClassifications] = useState("");
  const [schemaFormat, setSchemaFormat] = useState<KafkaSchemaFormat>(
    KafkaSchemaFormat.JSON,
  );
  const [schemaContent, setSchemaContent] = useState("");
  const [schemaVersion, setSchemaVersion] = useState("1.0.0");
  const [schemaSubject, setSchemaSubject] = useState("");
  const [schemaRegistryUrl, setSchemaRegistryUrl] = useState("");
  const [compatibilityLevel, setCompatibilityLevel] = useState<
    SchemaCompatibilityLevel | ""
  >("");
  const [partitionCount, setPartitionCount] = useState("");
  const [replicationFactor, setReplicationFactor] = useState("");
  const [retentionMs, setRetentionMs] = useState("");
  const [samplePayload, setSamplePayload] = useState("");
  const [dataSensitivity, setDataSensitivity] = useState<DataSensitivity | "">(
    "",
  );
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSchemaFile = async (file: File) => {
    const text = await file.text();
    if (schemaFormat === KafkaSchemaFormat.JSON) {
      try {
        JSON.parse(text);
      } catch {
        setErrorMessage("Schema file must be valid JSON for JSON format");
        return;
      }
    }
    setSchemaContent(text);
    if (!schemaSubject.trim()) {
      const base = file.name.replace(/\.(json|avsc|txt)$/i, "");
      setSchemaSubject(base);
    }
  };

  const handleSave = async () => {
    if (!tenantId) return;
    if (!name.trim() || !environment.trim() || !schemaContent.trim()) {
      setErrorMessage("Topic name, environment, and schema content are required");
      return;
    }
    setSaving(true);
    setErrorMessage(null);
    try {
      await apiService.createKafkaEventTopic({
        tenantId,
        name: name.trim(),
        environment: environment.trim(),
        purpose: purpose.trim() || undefined,
        description: description.trim() || undefined,
        classifications: classifications.trim() || undefined,
        status: EntityStatus.ACTIVE,
        schemaFormat,
        schemaContent,
        schemaVersion: schemaVersion.trim() || undefined,
        schemaSubject: schemaSubject.trim() || undefined,
        schemaRegistryUrl: schemaRegistryUrl.trim() || undefined,
        compatibilityLevel: compatibilityLevel || undefined,
        partitionCount: partitionCount ? Number(partitionCount) : undefined,
        replicationFactor: replicationFactor ? Number(replicationFactor) : undefined,
        retentionMs: retentionMs.trim() || undefined,
        samplePayload: samplePayload.trim() || undefined,
        dataSensitivity: dataSensitivity || undefined,
        notes: notes.trim() || undefined,
      });
      setSuccessOpen(true);
      setTimeout(goBack, 800);
    } catch (err: unknown) {
      setErrorMessage(
        err instanceof Error ? err.message : "Failed to create Kafka event topic",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <PageTitle
        pageTitle="Add Kafka event topic"
        pageDescription="Register a Kafka topic and its schema in the event registry."
      />

      <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 } }}>
        <Paper sx={{ mt: 3, mb: 3, p: 3, maxWidth: "80%", border: "1px solid #e0e0e0" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Topic name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Environment"
              value={environment}
              onChange={(e) => setEnvironment(e.target.value)}
              required
              fullWidth
              helperText="e.g. dev, staging, prod"
            />
            <TextField
              label="Purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              label="Classifications (JSON)"
              value={classifications}
              onChange={(e) => setClassifications(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <TextField
              select
              label="Schema format"
              value={schemaFormat}
              onChange={(e) => setSchemaFormat(e.target.value as KafkaSchemaFormat)}
              required
              fullWidth
            >
              {SCHEMA_FORMATS.map((f) => (
                <MenuItem key={f} value={f}>
                  {f}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button variant="outlined" onClick={() => fileInputRef.current?.click()}>
                Upload schema file
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.avsc,.txt,text/plain,application/json"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleSchemaFile(file);
                }}
              />
            </Box>
            <TextField
              label="Schema content"
              value={schemaContent}
              onChange={(e) => setSchemaContent(e.target.value)}
              required
              fullWidth
              multiline
              minRows={6}
            />
            <TextField
              label="Schema version"
              value={schemaVersion}
              onChange={(e) => setSchemaVersion(e.target.value)}
              fullWidth
            />
            <TextField
              label="Schema subject"
              value={schemaSubject}
              onChange={(e) => setSchemaSubject(e.target.value)}
              fullWidth
            />
            <TextField
              label="Schema registry URL"
              value={schemaRegistryUrl}
              onChange={(e) => setSchemaRegistryUrl(e.target.value)}
              fullWidth
            />
            <TextField
              select
              label="Compatibility level"
              value={compatibilityLevel}
              onChange={(e) =>
                setCompatibilityLevel(e.target.value as SchemaCompatibilityLevel | "")
              }
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {COMPATIBILITY_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Partitions"
                type="number"
                value={partitionCount}
                onChange={(e) => setPartitionCount(e.target.value)}
                fullWidth
              />
              <TextField
                label="Replication factor"
                type="number"
                value={replicationFactor}
                onChange={(e) => setReplicationFactor(e.target.value)}
                fullWidth
              />
              <TextField
                label="Retention (ms)"
                type="number"
                value={retentionMs}
                onChange={(e) => setRetentionMs(e.target.value)}
                fullWidth
              />
            </Box>
            <TextField
              select
              label="Data sensitivity"
              value={dataSensitivity}
              onChange={(e) =>
                setDataSensitivity(e.target.value as DataSensitivity | "")
              }
              fullWidth
            >
              <MenuItem value="">None</MenuItem>
              {SENSITIVITY_LEVELS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Sample payload"
              value={samplePayload}
              onChange={(e) => setSamplePayload(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              <Button variant="outlined" onClick={goBack}>
                Cancel
              </Button>
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? "Saving…" : "Save topic"}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      <Snackbar open={successOpen} autoHideDuration={3000}>
        <Alert severity="success">Kafka event topic created</Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={6000} onClose={() => setErrorMessage(null)}>
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AddKafkaEventTopic;
